import { createSignal } from "solid-js";

/** The pages that belong to one chosen pattern. */
export const patternViews = ["workspace", "print"] as const;
export type PatternView = (typeof patternViews)[number];

/** The pages of a print set: each garment's workspace, tiled paper print, and the pieces laid out on the cloth. */
export const setViews = ["workspace", "print", "layout"] as const;
export type SetView = (typeof setViews)[number];

/**
 * The app opens on a start screen, then runs as four steps: measurements,
 * choose a pattern, workspace, print.
 *
 * URLs:
 *   /                              start screen (title and menu)
 *   /measurements                  1. client measurements (for the open pattern)
 *   /patterns                      2. pattern library
 *   /patterns/:id                  3. workspace for a pattern
 *   /patterns/:id/print            4. A4 / A3 tiled print preview
 *   /patterns/:id/measurements     measurements with that pattern opened
 *   /sets/:id                      several patterns printed together (easy print)
 *   /sets/:id/workspace/:pattern   one of them in the workspace, with a tab for each
 *   /sets/:id/layout               the same patterns laid out on the cloth (easy layout)
 *   /pockets                       pocket wizard: step-by-step pocket making in 3D
 *   /pockets/:id/:step             one pocket's guide, open at a step (from 1)
 *   /skills                        skills library: every skill, by shelf, with what is practised
 *   /projects                      your projects
 *   /projects/new                  start a project: a recipe, a body and a design
 *   /projects/:id                  one project's steps, from draft and toile to the final press
 *   (`group` is the set of patterns ticked in the library; other ids are presets.)
 */
export type Route =
  | { name: "start" }
  | { name: "measurements"; patternId?: string }
  | { name: "library" }
  | { name: "pattern"; patternId: string; view: PatternView }
  | { name: "set"; setId: string; view: SetView; patternId?: string }
  | { name: "pockets"; pocketId?: string; step?: number }
  | { name: "skills" }
  | { name: "projects"; projectId?: string }
  | { name: "new-project"; recipeId?: string }
  | { name: "not-found"; path: string };

/** Older or friendlier names for the same views. */
const viewAliases: Record<string, PatternView> = { cad: "workspace" };
const measurementAliases = ["measurements", "config", "fit"];

export function parseRoute(pathname: string): Route {
  const parts = pathname.split("/").filter(Boolean).map(decodeURIComponent);
  if (parts.length === 0) return { name: "start" };
  if (parts.length === 1 && measurementAliases.includes(parts[0])) return { name: "measurements" };
  if (parts.length === 1 && parts[0] === "patterns") return { name: "library" };
  if (parts[0] === "patterns" && parts.length <= 3) {
    if (parts[2] !== undefined && measurementAliases.includes(parts[2])) return { name: "measurements", patternId: parts[1] };
    const view = parts[2] === undefined ? "workspace" : (patternViews as readonly string[]).includes(parts[2]) ? (parts[2] as PatternView) : viewAliases[parts[2]];
    if (view) return { name: "pattern", patternId: parts[1], view };
  }
  if (parts[0] === "pockets" && parts.length <= 3) {
    const step = parts[2] === undefined ? undefined : Number(parts[2]);
    if (step === undefined || (Number.isInteger(step) && step >= 1)) return { name: "pockets", pocketId: parts[1], step };
  }
  if (parts.length === 1 && parts[0] === "skills") return { name: "skills" };
  if (parts[0] === "projects" && parts.length === 1) return { name: "projects" };
  if (parts[0] === "projects" && parts[1] === "new" && parts.length <= 3) return { name: "new-project", recipeId: parts[2] };
  if (parts[0] === "projects" && parts.length === 2) return { name: "projects", projectId: parts[1] };
  if (parts[0] === "sets" && parts.length >= 2 && parts.length <= 4 && (parts[2] === undefined || (setViews as readonly string[]).includes(parts[2])) && (parts.length < 4 || parts[2] === "workspace")) {
    return { name: "set", setId: parts[1], view: (parts[2] as SetView | undefined) ?? "print", patternId: parts[3] };
  }
  return { name: "not-found", path: pathname };
}

export function routePath(route: Route): string {
  if (route.name === "start") return "/";
  if (route.name === "measurements") return route.patternId ? `/patterns/${encodeURIComponent(route.patternId)}/measurements` : "/measurements";
  if (route.name === "library") return "/patterns";
  if (route.name === "not-found") return route.path;
  if (route.name === "pockets") return route.pocketId ? `/pockets/${encodeURIComponent(route.pocketId)}${route.step && route.step > 1 ? `/${route.step}` : ""}` : "/pockets";
  if (route.name === "skills") return "/skills";
  if (route.name === "projects") return route.projectId ? `/projects/${encodeURIComponent(route.projectId)}` : "/projects";
  if (route.name === "new-project") return route.recipeId ? `/projects/new/${encodeURIComponent(route.recipeId)}` : "/projects/new";
  if (route.name === "set") return `/sets/${encodeURIComponent(route.setId)}${route.view === "print" ? "" : `/${route.view}`}${route.view === "workspace" && route.patternId ? `/${encodeURIComponent(route.patternId)}` : ""}`;
  const base = `/patterns/${encodeURIComponent(route.patternId)}`;
  return route.view === "workspace" ? base : `${base}/${route.view}`;
}

const [route, setRoute] = createSignal<Route>(parseRoute(window.location.pathname));
window.addEventListener("popstate", () => setRoute(parseRoute(window.location.pathname)));

export { route };

export function navigate(target: Route | string, options: { replace?: boolean } = {}) {
  const path = typeof target === "string" ? target : routePath(target);
  if (path !== window.location.pathname) {
    if (options.replace) history.replaceState(null, "", path);
    else history.pushState(null, "", path);
  }
  setRoute(parseRoute(path));
}

/**
 * Click handler for in-app anchors: plain left clicks navigate without a page
 * load, while modified clicks (new tab, new window) keep the browser default.
 */
export function linkClick(event: MouseEvent & { currentTarget: HTMLAnchorElement }) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigate(event.currentTarget.pathname);
}
